import { Achievement_Types_Enum, AchievementFragment, UserAchievementFragment } from '@/gql/generated/graphql'
import { FALLBACK_PROFILE_PIC } from '@consts/common'
import { StaticImageData } from 'next/image'

// Type for image override configurations
type ImageOverride = string | ((profilePicture: string | null) => string | StaticImageData | null)

// Configuration for achievement image overrides
export const ACHIEVEMENT_IMAGE_OVERRIDES: Record<string, ImageOverride> = {
  'verified-collector': (profilePicture) => profilePicture || FALLBACK_PROFILE_PIC,
  'friend-of-art-blocks':
    'https://res.cloudinary.com/art-blocks/image/upload/v1750782429/friend_of_art_blocks_yhkqqd.png',
  'day-one-minter': 'https://res.cloudinary.com/art-blocks/image/upload/v1750782489/day_one_minter_rerwpd.png',
  'ab-artist': 'https://res.cloudinary.com/art-blocks/image/upload/v1750943723/Artist_zo8ybn.jpg'
  // Add other hardcoded image overrides here
  // 'achievement-slug': 'https://example.com/hardcoded-image.png',
  // 'another-achievement': 'https://example.com/another-image.png',
}

/**
 * Gets the override image URL for an achievement slug, or null if no override exists
 * @param achievementSlug - The slug of the achievement
 * @param profilePicture - The user's profile picture (used for some overrides)
 * @returns The override image URL or null if no override is configured
 */
export function getAchievementImageOverride(
  achievementSlug: string,
  profilePicture: string | null = null
): string | StaticImageData | null {
  const override = ACHIEVEMENT_IMAGE_OVERRIDES[achievementSlug]

  if (!override) {
    return null
  }

  return typeof override === 'function' ? override(profilePicture) : override
}

/**
 * Resolves the image URL for an achievement, applying any configured overrides
 * @param achievementSlug - The slug of the achievement
 * @param originalImageUrl - The original image URL from the achievement data
 * @param profilePicture - The user's profile picture (used for some overrides)
 * @returns The final image URL to use
 */
export function resolveAchievementImageUrl(
  achievementSlug: string,
  originalImageUrl: string,
  profilePicture: string | null = null
): string | StaticImageData {
  const overrideUrl = getAchievementImageOverride(achievementSlug, profilePicture)
  return overrideUrl || originalImageUrl
}

/**
 * Applies image override to an achievement object
 * @param achievement - The achievement object
 * @param profilePicture - The user's profile picture (optional)
 * @returns Achievement object with potentially overridden image
 */
export function applyAchievementImageOverride<T extends Pick<AchievementFragment, 'slug' | 'image'>>(
  achievement: T,
  profilePicture: string | null = null
): T {
  const overrideUrl = getAchievementImageOverride(achievement.slug, profilePicture)

  if (overrideUrl) {
    return {
      ...achievement,
      image: {
        ...achievement.image,
        url: overrideUrl
      }
    }
  }

  return achievement
}

/**
 * Applies image override to a user achievement object
 * @param userAchievement - The user achievement object
 * @param profilePicture - The user's profile picture (optional)
 * @returns User achievement object with potentially overridden image
 */
export function applyUserAchievementImageOverride(
  userAchievement: UserAchievementFragment,
  profilePicture: string | null = null
): UserAchievementFragment {
  const overriddenAchievement = applyAchievementImageOverride(userAchievement.achievement, profilePicture)

  // Only create a new object if the achievement actually changed
  if (overriddenAchievement === userAchievement.achievement) {
    return userAchievement
  }

  return {
    ...userAchievement,
    achievement: overriddenAchievement
  }
}

/**
 * Applies image overrides to an array of achievements
 * @param achievements - Array of achievement objects
 * @param profilePicture - The user's profile picture (optional)
 * @returns Array of achievements with overrides applied
 */
export function applyAchievementImageOverrides<T extends Pick<AchievementFragment, 'slug' | 'image'>>(
  achievements: T[],
  profilePicture: string | null = null
): T[] {
  return achievements.map((achievement) => applyAchievementImageOverride(achievement, profilePicture))
}

/**
 * Applies image overrides to an array of user achievements
 * @param userAchievements - Array of user achievement objects
 * @param profilePicture - The user's profile picture (optional)
 * @returns Array of user achievements with overrides applied
 */
export function applyUserAchievementImageOverrides(
  userAchievements: UserAchievementFragment[],
  profilePicture: string | null = null
): UserAchievementFragment[] {
  return userAchievements.map((userAchievement) => applyUserAchievementImageOverride(userAchievement, profilePicture))
}

// returns up to the 3 highest priority achievements
export function getFeaturedAchievements(achievements: UserAchievementFragment[]): UserAchievementFragment[] {
  const getPriority = (type: Achievement_Types_Enum | null | undefined) => {
    switch (type) {
      case Achievement_Types_Enum.Historical:
        return 1
      case Achievement_Types_Enum.SetCompletion:
        return 2
      case Achievement_Types_Enum.Milestone:
        return 3
      default:
        return 4 // Any other types go last
    }
  }

  return achievements
    .filter((ua) => !!ua.achievement) // filter null/undefined achievements
    .sort((a, b) => {
      const priorityA = getPriority(a.achievement.type)
      const priorityB = getPriority(b.achievement.type)

      // Sort by priority first
      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }

      // If same priority, sort by earned date (newest first)
      return new Date(b.earned_at).getTime() - new Date(a.earned_at).getTime()
    })
    .slice(0, 3)
}

/**
 * Builds the share text for Twitter based on user achievements
 */
export function buildShareProfileText(userAchievements: UserAchievementFragment[]): string {
  const featuredAchievements = getFeaturedAchievements(userAchievements)

  let shareText = 'Explore my collecting journey on @artblocks_io.'

  // add achievement text if user has claimed achievements
  if (featuredAchievements.length > 0) {
    const achievementNames = featuredAchievements.map((ua: any) => ua.achievement.name)

    let formattedNames: string
    if (achievementNames.length === 1) {
      formattedNames = achievementNames[0]
    } else if (achievementNames.length === 2) {
      formattedNames = `${achievementNames[0]} and ${achievementNames[1]}`
    } else {
      formattedNames = `${achievementNames[0]}, ${achievementNames[1]}, and ${achievementNames[2]}`
    }

    shareText += ` I've been recognized as a ${formattedNames}.`
  }
  shareText += '\n\n' // formatting for tweet

  return shareText
}
